import { useLayoutEffect, useRef, useState } from 'react';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { TextWithReferences } from '@/new/components/text-references';
import { cn } from '@/new/lib/utils';

export function MatterSummary({
  content,
  matterId,
  context = 'Matter',
}: {
  content: string;
  matterId: string;
  context?: 'Matter' | 'Subset';
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    // Measured while clamped (the initial state): a taller scrollHeight means there's more to reveal.
    if (!isExpanded) setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [content, isExpanded]);

  const isClamped = !isExpanded;

  return (
    <div className="my-4.5">
      <h2 className="my-2.5 font-medium">{context} summary</h2>

      {content.length === 0 ? (
        <p className="text-[13px] text-gray-400">No {context} summary available.</p>
      ) : (
        <div
          ref={contentRef}
          className={cn(
            'prose prose-sm max-w-none text-[13px] leading-relaxed text-gray-700 [&_p]:my-3 [&_p:first-child]:mt-0',
            isClamped && 'mb-2 line-clamp-2',
          )}
        >
          <TextWithReferences content={content} />
        </div>
      )}

      {isOverflowing && (
        <Button
          variant="ghost"
          size="sm"
          className="px-0 text-blue-500 hover:text-blue-600"
          onClick={() =>
            setIsExpanded((prev) => {
              if (!prev) trackCustomEvent(TrackingEventsV2.MatterExpandSummaryFromOverview, { matterId, context });
              return !prev;
            })
          }
        >
          {isExpanded ? 'Show less' : 'Show more'}
          <Icon className={cn(isExpanded ? 'rotate-180' : '', 'lucide--chevron-down')} />
        </Button>
      )}
    </div>
  );
}
